// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import { DataTableBody, DataTableCell, DataTableHead, DataTableHeader, DataTableRow, Link } from "@insite/mobius/index";
import { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FlexWrapper, { FlexWrapperPresentationProps } from "@store/Library/Components/Toolkit/FlexWrapper";
import ArrowForward from "@store/Library/Components/Toolkit/Icons/ArrowForward";
import Button from "@store/MobiusOverrides/Button/Button";
import { resultCountProps } from "@store/Themes/common/props/resultCount";
import constants from "@store/constants";
import getDaysDifference from "@store/Framework/Common/Utilities/getDaysDifference";
import { TaxExemptionCertificatesDataViewContext } from "@store/Framework/Store/Greif/Data/TaxExemptionCertificates/TaxExemptionCertificatesSelectors";
import updateSearchFields from "@store/Framework/Store/Greif/Pages/TaxExemptionCertificates/Handlers/UpdateSearchFields";
import Status, { StatusProps } from "@store/Library/Components/Status";
import DataViewLoadingOverlay from "@store/Library/Components/Toolkit/DataViewLoadingOverlay";
import { historyTableProps } from "@store/Themes/common/props/history";
import { tableProps } from "@store/Themes/common/props/table";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import React, { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const Pagination = getWidgetComponent("TaxExemptionCertificates/TaxExemptionCertificatesPagination");

const enum fields {
    showAddNewButton = "showAddNewButton",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showAddNewButton]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    getTaxExemptionCertificatesParameter:
        state.greif.pages.taxExemptionCertificates.getTaxExemptionCertificatesParameter,
    language: state.context.session.language,
    contactSupportPageLink: getPageLinkByPageType(state, constants.pageType.tecContactSupport),
    submitNewCertificatePageLink: getPageLinkByPageType(state, constants.pageType.tecSubmitNew),
});

const mapDispatchToProps = {
    updateSearchFields,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface TaxExemptionCertificatesTableStyles {
    container?: InjectableCss;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    noResultsNewLink?: TypographyProps;
    countText?: TypographyProps;
    loadingOverlay?: LoadingOverlayProps;
    headingWrapper?: FlexWrapperPresentationProps;
    tableWrapper?: InjectableCss;
    dataTable?: DataTableProps;
    statusHeader?: DataTableHeaderProps;
    orderDateCells?: DataTableCellProps;
    actionCells?: DataTableCellProps;
    statusStyles?: StatusProps;
    actionButtonProps?: ButtonPresentationProps;
    submitNewButtonProps?: ButtonPresentationProps;
}

export const taxExemptionCertificatesTableStyles: TaxExemptionCertificatesTableStyles = {
    actionCells: {},
    noResultsText: historyTableProps.noResultsText,
    noResultsNewLink: mergeProps<TypographyProps>(historyTableProps.noResultsText, {
        variant: "h6",
    }),
    countText: {
        css: css`
            ${resultCountProps.countText?.css}
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
    headingWrapper: {
        extendedStyles: {
            wrapper: {
                css: css`
                    ${x => spacingStyles(null, x.theme.design.typography.spacing.base)}
                `,
            },
            content: {
                css: css`
                    align-items: flex-end;
                    justify-content: space-between;
                `,
            },
        },
    },
    tableWrapper: tableProps.wrapper,
    statusStyles: {
        extendedStyles: {
            content: {
                css: css`
                    ${({ theme }) => `
                        font-size ${theme.design.typography.size.tiny};
                        font-weight ${theme.design.typography.weight.bold};
                    `}
                `,
            },
        },
    },
    actionButtonProps: {
        variant: "tertiary",
        sizeVariant: "small",
        icon: { position: "right", src: ArrowForward },
        css: css`
            ${x => x.theme.button.tertiary.css}
            width: 100%;
        `,
    },
    submitNewButtonProps: { variant: "primary", icon: { position: "right", src: ArrowForward } },
};

const statusToColor = (status: string) => {
    switch (status) {
        case "Valid":
            return "green";
        case "Expiring Soon":
            return "yellow";
        case "Expired":
            return "red";
        default:
            return "grey";
    }
};

const styles = taxExemptionCertificatesTableStyles;

const TaxExemptionCertificatesTable = ({
    language,
    getTaxExemptionCertificatesParameter,
    history,
    contactSupportPageLink,
    submitNewCertificatePageLink,
    updateSearchFields,
    fields: { showAddNewButton },
}: Props) => {
    const taxExemptionCertificatesDataView = usePersistentDataView(useContext(TaxExemptionCertificatesDataViewContext));

    const certificatesCount = (taxExemptionCertificatesDataView as any)?.pagination?.totalItemCount ?? 0;
    const certificatesCountLabel = certificatesCount > 1 ? translate("Certificates") : translate("Certificate");

    const headerClick = (sortField: string) => {
        const sort = getTaxExemptionCertificatesParameter.sort === sortField ? `${sortField} DESC` : sortField;
        updateSearchFields({ sort });
    };

    const sorted = (sortField: string) => {
        let sorted: boolean | string = false;
        if (getTaxExemptionCertificatesParameter.sort === sortField) {
            sorted = "ascending";
        } else if (getTaxExemptionCertificatesParameter.sort === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    const mapDaysToStatus = (date: Date | string) => {
        const expiryDate = getLocalizedDateTime({
            dateTime: new Date(date),
            language,
        });

        const daysDiff = getDaysDifference(expiryDate);

        if (daysDiff > 90) {
            return "Valid";
        }

        if (daysDiff > 0) {
            return "Expiring Soon";
        }

        if (daysDiff < 0) {
            return "Expired";
        }

        return undefined;
    };

    const rows = taxExemptionCertificatesDataView?.value?.map(certificate => {
        return {
            id: certificate.id,
            state: certificate.state,
            status: mapDaysToStatus(certificate.expiryDate) || certificate.status,
            financeCompanyName: certificate.financeCompanyName,
            expiryDate:
                certificate.expiryDate && !certificate.expiryDate.includes("9999")
                    ? getLocalizedDateTime({
                          dateTime: new Date(certificate.expiryDate),
                          language,
                      })
                    : translate("Does not expire"),
        };
    });

    const handleContactButtonClick = (id: string) => {
        contactSupportPageLink && history.push(`${contactSupportPageLink.url}?certificateId=${id}`);
    };

    const handleAddNewClick = () => {
        submitNewCertificatePageLink && history.push(`${submitNewCertificatePageLink.url}`);
    };

    return (
        <>
            <DataViewLoadingOverlay dataView={taxExemptionCertificatesDataView} {...styles.loadingOverlay}>
                {!rows?.length ? (
                    <StyledWrapper {...styles.noResultsContainer}>
                        <Typography
                            {...styles.noResultsText}
                            data-test-selector="taxExemptionCertificatesTable_noCertificatesFound"
                        >
                            {translate("No certificates found")}
                        </Typography>
                        {showAddNewButton && (
                            <Typography {...styles.noResultsNewLink}>
                                {translate("You can")}{" "}
                                <Link onClick={() => handleAddNewClick()}>{translate("submit a new certificate")}</Link>
                                .
                            </Typography>
                        )}
                    </StyledWrapper>
                ) : (
                    <>
                        <StyledWrapper {...styles.container} data-test-selector="taxExemptionCertificatesTable">
                            {certificatesCount && (
                                <FlexWrapper {...styles.headingWrapper}>
                                    <Typography
                                        {...styles.countText}
                                    >{`${certificatesCount} ${certificatesCountLabel}`}</Typography>
                                    {showAddNewButton && (
                                        <Button {...styles.submitNewButtonProps} onClick={() => handleAddNewClick()}>
                                            {translate("Submit New Certificate")}
                                        </Button>
                                    )}
                                </FlexWrapper>
                            )}
                            <StyledWrapper {...styles.tableWrapper}>
                                <DataTable {...styles.dataTable}>
                                    <DataTableHead>
                                        <DataTableHeader
                                            {...styles.statusHeader}
                                            sorted={sorted("financeCompanyName")}
                                            onSortClick={() => headerClick("financeCompanyName")}
                                            data-test-selector="tableHeader_greifCompany"
                                        >
                                            {translate("Greif Company")}
                                        </DataTableHeader>
                                        <DataTableHeader
                                            {...styles.statusHeader}
                                            sorted={sorted("state")}
                                            onSortClick={() => headerClick("state")}
                                            data-test-selector="tableHeader_state"
                                        >
                                            {translate("State")}
                                        </DataTableHeader>
                                        <DataTableHeader
                                            {...styles.statusHeader}
                                            sorted={sorted("status")}
                                            onSortClick={() => headerClick("status")}
                                            data-test-selector="tableHeader_status"
                                        >
                                            {translate("TEC Status")}
                                        </DataTableHeader>
                                        <DataTableHeader
                                            {...styles.statusHeader}
                                            sorted={sorted("expiryDate")}
                                            onSortClick={() => headerClick("expiryDate")}
                                            data-test-selector="tableHeader_expiryDate"
                                        >
                                            {translate("TEC Expiry Date")}
                                        </DataTableHeader>
                                        <DataTableHeader
                                            {...styles.statusHeader}
                                            data-test-selector="tableHeader_actions"
                                        >
                                            {translate("Action")}
                                        </DataTableHeader>
                                    </DataTableHead>
                                    <DataTableBody data-test-selector="taxExemptionCertificatesTable_tableBody">
                                        {rows?.map(row => (
                                            <DataTableRow key={row.id}>
                                                <DataTableCell
                                                    {...styles.orderDateCells}
                                                    data-test-selector="orderHistoryTable_tableCell_requestedDeliveryDateDisplay"
                                                >
                                                    {row.financeCompanyName}
                                                </DataTableCell>
                                                <DataTableCell
                                                    {...styles.orderDateCells}
                                                    data-test-selector="orderHistoryTable_tableCell_requestedDeliveryDateDisplay"
                                                >
                                                    {row.state}
                                                </DataTableCell>
                                                <DataTableCell
                                                    {...styles.orderDateCells}
                                                    data-test-selector="orderHistoryTable_tableCell_requestedDeliveryDateDisplay"
                                                >
                                                    <Status
                                                        type={statusToColor(String(row.status))}
                                                        align="center"
                                                        {...styles.statusStyles}
                                                    >
                                                        {row.status}
                                                    </Status>
                                                </DataTableCell>
                                                <DataTableCell
                                                    {...styles.orderDateCells}
                                                    data-test-selector="orderHistoryTable_tableCell_requestedDeliveryDateDisplay"
                                                >
                                                    {row.expiryDate}
                                                </DataTableCell>
                                                <DataTableCell
                                                    {...styles.actionCells}
                                                    data-test-selector="orderHistoryTable_tableCell_requestedDeliveryDateDisplay"
                                                >
                                                    <Button
                                                        {...styles.actionButtonProps}
                                                        onClick={() => handleContactButtonClick(row.id)}
                                                    >
                                                        {translate("Renew Certificate")}
                                                    </Button>
                                                </DataTableCell>
                                            </DataTableRow>
                                        ))}
                                    </DataTableBody>
                                </DataTable>
                            </StyledWrapper>
                        </StyledWrapper>
                        <Pagination />
                    </>
                )}
            </DataViewLoadingOverlay>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(TaxExemptionCertificatesTable)),
    definition: {
        displayName: "Certificates Table",
        group: constants.widgetGroup.taxExemptionCertificates,
        allowedContexts: ["TaxExemptionCertificatesPage" as any],
        fieldDefinitions: [
            {
                name: fields.showAddNewButton,
                displayName: "Show Add New Button",
                fieldType: "General",
                editorTemplate: "CheckboxField",
                variant: "toggle",
                defaultValue: true,
            },
        ],
    },
};

export default widgetModule;
